import React, { useState, useEffect, useRef } from "react";
import { BrandLogo } from "@/components/Brand";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useCountry } from "@/lib/country";
import { CountryPickerCompact } from "@/pages/Landing";
import { api, setToken, formatApiErrorDetail } from "@/lib/api";
import { track, identify, trackSignUp, EVENTS } from "@/lib/analytics";
import { Compass, ArrowLeft, Sparkles, Stethoscope, ShieldCheck, Check, MailCheck } from "lucide-react";
import { toast } from "sonner";

/* Login page — secure passwordless, email-first:
 *   1. Enter your email → we check the account:
 *        • brand new → ask for their name, then email a 6-digit code + magic link
 *        • returning email-only → email the code + link straight away
 *        • has a password → ask for it (one-time code as fallback)
 *   2. Verify the 6-digit code (or tap the link in the email) — a session is
 *      only created AFTER the inbox is proven.
 *   3. "I have an access code from my provider" stays as a separate
 *      collapsed block below for partner-referred families.
 */
/**
 * Post-auth redirect helper — reads (and clears) the deep-link that
 * ProtectedRoute stashed in sessionStorage, or falls back to /dashboard.
 */
function popPostAuthRedirect(fallback = "/dashboard") {
    try {
        const stashed = sessionStorage.getItem("mlcn_post_login_redirect");
        if (stashed) {
            sessionStorage.removeItem("mlcn_post_login_redirect");
            return stashed;
        }
    } catch (_e) { /* private mode / disabled */ }
    return fallback;
}

export default function Login() {
    const { user, login, refresh } = useAuth();
    const { country, setCountry } = useCountry();
    const navigate = useNavigate();
    const location = useLocation();
    // Flow: "email" → check → "new" (name) | "password" | "code" (verify OTP).
    const [step, setStep] = useState("email");
    const [email, setEmail] = useState(() => {
        try { return new URLSearchParams(window.location.search).get("email") || ""; } catch { return ""; }
    });
    const [name, setName] = useState("");
    const [password, setPassword] = useState("");
    const [firstName, setFirstName] = useState("");
    const [code, setCode] = useState("");
    const [isNewAccount, setIsNewAccount] = useState(false);
    const [postAuthUser, setPostAuthUser] = useState(null);
    const [resendIn, setResendIn] = useState(0);
    const [busy, setBusy] = useState(false);
    // Persistent inline feedback (toasts are transient and easy to miss).
    const [codeError, setCodeError] = useState("");
    const [throttleMsg, setThrottleMsg] = useState("");
    // Guards the "already signed in → welcome back" redirect so it never
    // double-fires after a login completed on this very page.
    const authStartedRef = useRef(false);

    // Deep links from drip emails carry ?email= (e.g. /dashboard?email=x).
    useEffect(() => {
        if (email) return;
        const fromSearch = location.state?.from?.search;
        if (!fromSearch) return;
        const e = new URLSearchParams(fromSearch).get("email");
        if (e) setEmail(e);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Landing / Beta / article CTAs already requested a code before sending
    // the caregiver here — jump straight to the code step.
    useEffect(() => {
        if (location.state?.codeSent && email) {
            setStep("code");
            setResendIn(60);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Returning caregiver with a live session skips login entirely.
    useEffect(() => {
        if (authStartedRef.current) return;
        if (user && typeof user === "object" && user.id) {
            const first = ((user.name || "").trim().split(" ") || [""])[0];
            toast.success(`Welcome back${first ? `, ${first}` : ""} — you're already signed in.`);
            navigate(popPostAuthRedirect("/dashboard"), { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    // Resend cooldown ticker.
    useEffect(() => {
        if (resendIn <= 0) return;
        const t = setInterval(() => setResendIn((s) => (s > 0 ? s - 1 : 0)), 1000);
        return () => clearInterval(t);
    }, [resendIn]);

    // Access-code flow state machine — two screens:
    //   "code"     → caregiver enters the partner code
    //   "create"   → after code is verified, they create the account
    const [acStep, setAcStep] = useState("code");
    const [newlyCreated, setNewlyCreated] = useState(false);
    const [newPw, setNewPw] = useState("");
    const [acCode, setAcCode] = useState("");
    const [acPartner, setAcPartner] = useState(""); // populated by /validate
    const [acCodeKind, setAcCodeKind] = useState("access_code"); // "access_code" | "org_code"
    const [acForm, setAcForm] = useState({ name: "", email: "", password: "" });
    const [acBusy, setAcBusy] = useState(false);
    const [acExpanded, setAcExpanded] = useState(false);

    const cleanEmail = () => email.trim().toLowerCase();

    /** Attribution for this signup — ?utm_campaign= wins, else a stored
     *  Caregiver Tools source (set when they clicked a tool CTA/download). */
    const signupUtm = () => {
        try {
            const q = new URLSearchParams(window.location.search).get("utm_campaign");
            if (q) return q.slice(0, 64);
            const a = JSON.parse(localStorage.getItem("ml_tool_attr") || "null");
            if (a && a.tool_day) return `tool-day-${a.tool_day}`;
        } catch { /* noop */ }
        return "";
    };

    /** Where to send them after auth — org admins → /org-admin, else deep link / dashboard. */
    const postAuthNavigate = async (u) => {
        const from = location.state?.from;
        const fromUrl = from
            ? `${from.pathname || ""}${from.search || ""}${from.hash || ""}`
            : null;
        let fallback = (u?.status || "approved") === "approved" ? "/dashboard" : "/pending";
        if (fallback === "/dashboard" && u?.role !== "admin") {
            try {
                const orgMe = await api.get("/org/me");
                if ((orgMe.data?.is_org_admin_of || []).length > 0) fallback = "/org-admin";
            } catch { /* fall back to /dashboard */ }
        }
        navigate(fromUrl || popPostAuthRedirect(fallback), { replace: true });
    };

    /** Request the 6-digit code + magic link email, then show the code step. */
    const startPasswordless = async (withName = "") => {
        try {
            const r = await api.post("/auth/passwordless/start", {
                email: cleanEmail(), country, ...(withName ? { name: withName } : {}),
                ...(signupUtm() ? { utm_campaign: signupUtm() } : {}),
            });
            setIsNewAccount(Boolean(r.data.new_account));
            setCode("");
            setCodeError("");
            setThrottleMsg("");
            setStep("code");
            setResendIn(60);
            toast.success("Check your email — your code and sign-in link are on the way.");
        } catch (err) {
            if (err?.response?.status === 429) {
                // A code is already in flight (cooldown / rate limit) — no new
                // email was sent. Tell them clearly and let them type the one
                // they already have.
                const msg = formatApiErrorDetail(err.response?.data?.detail)
                    || "We just sent you a code — check your inbox before requesting another.";
                setCode("");
                setStep("code");
                setResendIn(60);
                setThrottleMsg(msg);
                toast(msg);
                return;
            }
            throw err;
        }
    };

    const continueWithEmail = async (e) => {
        e.preventDefault();
        if (!/^\S+@\S+\.\S+$/.test(cleanEmail())) { toast.error("That email looks off."); return; }
        setBusy(true);
        try {
            const r = await api.post("/auth/email-check", { email: cleanEmail() });
            setFirstName(r.data.first_name || "");
            if (!r.data.exists) {
                setStep("new");
            } else if (r.data.has_password) {
                setStep("password");
            } else {
                // Returning email-only caregiver — email them the code + link.
                await startPasswordless();
            }
        } catch (err) {
            if (err?.response?.status === 409) { setStep("password"); }
            else toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't check that email — try again.");
        } finally {
            setBusy(false);
        }
    };

    const createAccount = async (e) => {
        e.preventDefault();
        if (!name.trim()) { toast.error("What should we call you?"); return; }
        track(EVENTS.SIGNUP_STARTED, { form: "login_email_first" });
        setBusy(true);
        try {
            await startPasswordless(name.trim());
        } catch (err) {
            if (err?.response?.status === 409) { setStep("password"); }
            else toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't send your code — try again.");
        } finally {
            setBusy(false);
        }
    };

    /** Exchange the typed 6-digit code for a session. */
    const verifyLoginCode = async (e) => {
        e?.preventDefault?.();
        const digits = code.replace(/\D/g, "");
        if (digits.length !== 6) {
            setCodeError("Enter the 6-digit code from your email.");
            toast.error("Enter the 6-digit code from your email.");
            return;
        }
        setCodeError("");
        setBusy(true);
        try {
            const r = await api.post("/auth/passwordless/verify-code", { email: cleanEmail(), code: digits });
            authStartedRef.current = true;
            setToken(r.data.token);
            if (r.data.user?.primary_session_id) {
                localStorage.setItem("mlcn_session_id", r.data.user.primary_session_id);
            }
            await refresh();
            if (r.data.new_account) {
                trackSignUp(r.data.user, { method: "passwordless_code", country });
            } else {
                identify(r.data.user?.id);
                track(EVENTS.LOGIN, { method: "passwordless_code", country });
            }
            toast.success(r.data.new_account ? "Welcome — take a look around." : "Welcome back.");
            // Nameless account (landing/article CTAs skip the name step) —
            // ask for a first name before entering, so the greeting is never
            // an email fragment. Skippable.
            if (!((r.data.user?.name || "").trim())) {
                setPostAuthUser(r.data.user || {});
                setNewlyCreated(Boolean(r.data.new_account));
                setName("");
                setStep("who");
                return;
            }
            // Brand-new account with a name — offer an optional password
            // before entering (skippable; email sign-in always works).
            if (r.data.new_account) {
                setPostAuthUser(r.data.user || {});
                setNewlyCreated(true);
                setStep("secure");
                return;
            }
            await postAuthNavigate(r.data.user);
        } catch (err) {
            const msg = formatApiErrorDetail(err.response?.data?.detail) || "That code didn't work — try again.";
            setCodeError(msg);
            toast.error(msg);
        } finally {
            setBusy(false);
        }
    };

    const resendCode = async () => {
        if (resendIn > 0 || busy) return;
        setBusy(true);
        try {
            await startPasswordless(isNewAccount ? name.trim() : "");
        } catch (err) {
            const msg = formatApiErrorDetail(err.response?.data?.detail) || "Couldn't resend right now.";
            setThrottleMsg(msg);
            toast.error(msg);
        } finally {
            setBusy(false);
        }
    };

    /** "Who" step — save the first name (or skip), then offer the optional
     *  password step to brand-new accounts before entering the app. */
    const finishWho = async (e) => {
        e?.preventDefault?.();
        const first = name.trim();
        if (first) {
            setBusy(true);
            try {
                await api.post("/me/name", { name: first });
                await refresh();
            } catch { /* non-blocking — they're already signed in */ }
            setBusy(false);
        }
        if (newlyCreated) { setStep("secure"); return; }
        await postAuthNavigate(postAuthUser);
    };

    const pwChecks = {
        length: newPw.length >= 8,
        letter: /[A-Za-z]/.test(newPw),
        number: /\d/.test(newPw),
    };
    const pwOk = pwChecks.length && pwChecks.letter && pwChecks.number;

    /** "Secure" step — optional password for the brand-new account. */
    const finishSecure = async (e) => {
        e?.preventDefault?.();
        if (!pwOk) { toast.error("Use at least 8 characters with a letter and a number."); return; }
        setBusy(true);
        try {
            await api.post("/me/set-password", { password: newPw });
            setNewPw("");
            toast.success("Password saved — you can now sign in with email + password too.");
            await postAuthNavigate(postAuthUser);
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't save the password.");
        } finally {
            setBusy(false);
        }
    };

    const passwordSignIn = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            authStartedRef.current = true;
            const u = await login(cleanEmail(), password);
            await postAuthNavigate(u);
        } catch (err) {
            authStartedRef.current = false;
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Login failed.");
        } finally {
            setBusy(false);
        }
    };

    /** Password-account fallback — email a one-time code instead. */
    const sendCodeInstead = async () => {
        setBusy(true);
        try {
            await startPasswordless();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't send a code right now.");
        } finally {
            setBusy(false);
        }
    };

    const verifyCode = async (e) => {
        e.preventDefault();
        const c = acCode.trim().toUpperCase();
        if (!c) { toast.error("Enter the code your provider gave you."); return; }
        setAcBusy(true);
        try {
            // Two independent code systems live side-by-side:
            //  1) Partner access codes (format ML-XXXX) → /access-codes/validate
            //  2) Org invite codes (format like G7C68GRE) → /org/validate
            let matched = null;
            try {
                const r = await api.post("/access-codes/validate", { code: c });
                matched = { kind: "access_code", partner: r.data.partner_name || "your provider" };
            } catch (err) {
                if (err?.response?.status === 404) {
                    const r2 = await api.post("/org/validate", { code: c });
                    matched = { kind: "org_code", partner: r2.data.partner_name || r2.data.org_name || "your care team" };
                } else {
                    throw err;
                }
            }
            setAcPartner(matched.partner);
            setAcCodeKind(matched.kind);
            setAcStep("create");
            toast.success(`Welcome — ${matched.partner} referred you.`);
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "We don't recognise that code.");
        } finally {
            setAcBusy(false);
        }
    };

    const registerWithCode = async (e) => {
        e.preventDefault();
        const acEmail = acForm.email.trim().toLowerCase();
        if (!acForm.name.trim()) { toast.error("Your name, please?"); return; }
        if (!/^\S+@\S+\.\S+$/.test(acEmail)) { toast.error("That email looks off."); return; }
        if ((acForm.password || "").length < 8) { toast.error("Password should be at least 8 characters."); return; }
        setAcBusy(true);
        try {
            const codeUpper = acCode.trim().toUpperCase();
            const payload = {
                name: acForm.name.trim(),
                email: acEmail,
                password: acForm.password,
            };
            if (acCodeKind === "org_code") {
                payload.org_code = codeUpper;
            } else {
                payload.access_code = codeUpper;
            }
            const r = await api.post("/auth/register", payload);
            authStartedRef.current = true;
            setToken(r.data.token);
            if (r.data.user?.primary_session_id) {
                localStorage.setItem("mlcn_session_id", r.data.user.primary_session_id);
            }
            await refresh();
            identify(r.data.user?.id);
            trackSignUp(r.data.user, { method: acCodeKind === "org_code" ? "org_code" : "access_code", partner: acPartner });
            toast.success(`Account created — welcome to Memory Lane.`);
            navigate(popPostAuthRedirect("/dashboard"));
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't create your account.");
        } finally {
            setAcBusy(false);
        }
    };

    return (
        <div className="min-h-screen bg-cream">
            <header className="bg-cream/80 backdrop-blur-sm border-b border-[#E2E8F0]/60">
                <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-4">
                    <Link to="/" className="flex items-center gap-2.5">
                        <BrandLogo className="h-6" />
                    </Link>
                    <Link to="/" data-testid="login-back" className="text-sm font-medium text-[#4C5551] hover:text-teal-dark inline-flex items-center gap-1">
                        <ArrowLeft size={16} strokeWidth={1.6} /> Home
                    </Link>
                </div>
            </header>

            <main className="max-w-md mx-auto px-4 sm:px-6 py-12 md:py-16 animate-fade-in-up">
                {/* 1) Email-first sign in / sign up */}
                <section
                    className="ml-card p-6 md:p-7 mb-6 !border-teal-light bg-white"
                    data-testid="login-email-first-block"
                >
                    <div className="flex items-start gap-3 mb-4">
                        <span className="w-10 h-10 rounded-2xl bg-teal text-white flex items-center justify-center shrink-0">
                            {step === "code"
                                ? <MailCheck strokeWidth={1.7} size={16} />
                                : <Sparkles strokeWidth={1.7} size={16} />}
                        </span>
                        <div>
                            <h1 className="font-heading font-semibold text-2xl text-[#12776F]">
                                {step === "password"
                                    ? `Welcome back${firstName ? `, ${firstName}` : ""}.`
                                    : step === "new"
                                        ? "Let's set you up."
                                        : step === "who"
                                            ? "You're in — one last thing."
                                            : step === "secure"
                                                ? "Would you like to create a password?"
                                                : step === "code"
                                                    ? "Check your email."
                                                    : "Sign in with your email."}
                            </h1>
                            <p className="text-sm text-[#4C5551] mt-0.5">
                                {step === "password"
                                    ? "This account has a password."
                                    : step === "new"
                                        ? "Looks like you're new here — just your name and you're in. No password needed."
                                        : step === "who"
                                            ? "What should we call you? A first name is plenty."
                                            : step === "secure"
                                                ? "Optional — you can always sign in with just your email. A password adds a second way in and extra security."
                                                : step === "code"
                                                    ? "We emailed you a 6-digit code and a one-tap sign-in link. Use whichever is easier."
                                                    : "New or returning — no password needed. We'll email you a code."}
                            </p>
                        </div>
                    </div>

                    {step === "email" && (
                        <form onSubmit={continueWithEmail} className="space-y-3" noValidate>
                            <div className="flex gap-2">
                                <CountryPickerCompact
                                    value={country}
                                    onChange={setCountry}
                                    testid="login-country"
                                />
                                <input
                                    type="email"
                                    data-testid="login-email-input"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="you@example.com"
                                    className="ml-input flex-1"
                                    autoComplete="email"
                                    autoFocus
                                />
                            </div>
                            <button
                                type="submit"
                                disabled={busy}
                                data-testid="login-continue-btn"
                                className="w-full bg-teal-dark hover:bg-teal-dark/90 active:bg-teal-dark/80 text-white rounded-2xl px-5 py-3 text-sm font-medium min-h-[44px] disabled:opacity-50"
                            >
                                {busy ? "One moment…" : "Continue"}
                            </button>
                        </form>
                    )}

                    {step === "new" && (
                        <form onSubmit={createAccount} className="space-y-3" noValidate>
                            <p className="text-xs text-[#6E7A74] px-1" data-testid="login-new-email-echo">
                                Creating an account for <strong className="text-[#1F2624]">{cleanEmail()}</strong>{" "}
                                <button type="button" className="underline text-teal-dark" data-testid="login-change-email"
                                    onClick={() => { setStep("email"); setName(""); }}>change</button>
                            </p>
                            <input
                                type="text"
                                data-testid="login-name-input"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="Your name"
                                className="ml-input w-full"
                                autoComplete="name"
                                autoFocus
                            />
                            <button
                                type="submit"
                                disabled={busy}
                                data-testid="login-create-btn"
                                className="w-full bg-teal-dark hover:bg-teal-dark/90 text-white rounded-2xl px-5 py-3 text-sm font-medium min-h-[44px] disabled:opacity-50"
                            >
                                {busy ? "Sending your code…" : "Continue — we'll email you a code"}
                            </button>
                            <p className="text-[11px] text-[#6E7A74] px-1">
                                No password needed. You can add one later in Settings if you'd like.
                            </p>
                        </form>
                    )}

                    {step === "who" && (
                        <form onSubmit={finishWho} className="space-y-3" noValidate data-testid="login-who-form">
                            <input
                                type="text"
                                data-testid="login-who-name-input"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="Your first name"
                                className="ml-input w-full"
                                autoComplete="given-name"
                                autoFocus
                            />
                            <button
                                type="submit"
                                disabled={busy}
                                data-testid="login-who-continue"
                                className="w-full bg-teal-dark hover:bg-teal-dark/90 text-white rounded-2xl px-5 py-3 text-sm font-medium min-h-[44px] disabled:opacity-50"
                            >
                                {busy ? "One moment…" : "Continue"}
                            </button>
                            <button
                                type="button"
                                disabled={busy}
                                data-testid="login-who-skip"
                                onClick={() => postAuthNavigate(postAuthUser)}
                                className="w-full text-sm text-[#6E7A74] underline underline-offset-2 min-h-[44px]"
                            >
                                Skip for now
                            </button>
                        </form>
                    )}

                    {step === "secure" && (
                        <form onSubmit={finishSecure} className="space-y-3" noValidate data-testid="login-secure-form">
                            <input
                                type="password"
                                data-testid="login-secure-password-input"
                                value={newPw}
                                onChange={(e) => setNewPw(e.target.value)}
                                placeholder="Choose a password"
                                className="ml-input w-full"
                                autoComplete="new-password"
                                autoFocus
                            />
                            <ul className="text-xs space-y-1" data-testid="login-secure-checklist">
                                {[["length", "At least 8 characters"], ["letter", "Contains a letter"], ["number", "Contains a number"]].map(([k, label]) => (
                                    <li key={k} className={pwChecks[k] ? "text-teal-dark font-medium" : "text-[#6E7A74]"}>
                                        {pwChecks[k] ? "✓" : "○"} {label}
                                    </li>
                                ))}
                            </ul>
                            <button
                                type="submit"
                                disabled={busy || !pwOk}
                                data-testid="login-secure-save"
                                className="w-full bg-teal-dark hover:bg-teal-dark/90 text-white rounded-2xl px-5 py-3 text-sm font-medium min-h-[44px] disabled:opacity-50"
                            >
                                {busy ? "One moment…" : "Save password"}
                            </button>
                            <button
                                type="button"
                                disabled={busy}
                                data-testid="login-secure-skip"
                                onClick={() => postAuthNavigate(postAuthUser)}
                                className="w-full text-sm text-[#6E7A74] underline underline-offset-2 min-h-[44px]"
                            >
                                Skip — email sign-in is all I need
                            </button>
                        </form>
                    )}

                    {step === "code" && (
                        <form onSubmit={verifyLoginCode} className="space-y-3" noValidate data-testid="login-code-form">
                            <p className="text-xs text-[#6E7A74] px-1" data-testid="login-code-email-echo">
                                Sent to <strong className="text-[#1F2624]">{cleanEmail()}</strong>{" "}
                                <button type="button" className="underline text-teal-dark" data-testid="login-code-change-email"
                                    onClick={() => { setStep("email"); setCode(""); setCodeError(""); setThrottleMsg(""); }}>change</button>
                            </p>
                            <input
                                type="text"
                                inputMode="numeric"
                                pattern="[0-9]*"
                                maxLength={6}
                                data-testid="login-code-input"
                                value={code}
                                onChange={(e) => { setCode(e.target.value.replace(/\D/g, "").slice(0, 6)); if (codeError) setCodeError(""); }}
                                placeholder="000000"
                                className={`ml-input w-full font-mono tracking-[0.5em] text-center text-2xl ${codeError ? "!border-red-400" : ""}`}
                                autoComplete="one-time-code"
                                autoFocus
                                aria-invalid={Boolean(codeError)}
                                aria-describedby={codeError ? "login-code-error" : undefined}
                            />
                            {codeError && (
                                <p
                                    id="login-code-error"
                                    data-testid="login-code-error"
                                    role="alert"
                                    className="text-sm text-red-600 px-1 flex items-start gap-1.5"
                                >
                                    {codeError}
                                </p>
                            )}
                            <button
                                type="submit"
                                disabled={busy || code.length !== 6}
                                data-testid="login-code-submit"
                                className="w-full bg-teal-dark hover:bg-teal-dark/90 text-white rounded-2xl px-5 py-3 text-sm font-medium min-h-[44px] disabled:opacity-50"
                            >
                                {busy ? "Verifying…" : "Sign in"}
                            </button>
                            <div className="flex items-center justify-between px-1">
                                <p className="text-[11px] text-[#6E7A74]">
                                    Code expires in 10 minutes.
                                </p>
                                <button
                                    type="button"
                                    onClick={resendCode}
                                    disabled={resendIn > 0 || busy}
                                    data-testid="login-resend-code"
                                    className="text-xs text-teal-dark underline underline-offset-2 disabled:opacity-50 disabled:no-underline"
                                >
                                    {resendIn > 0 ? `Send a new code (${resendIn}s)` : "Send a new code"}
                                </button>
                            </div>
                            {throttleMsg && (
                                <p
                                    data-testid="login-throttle-notice"
                                    role="status"
                                    className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2"
                                >
                                    {throttleMsg}{resendIn > 0 ? ` You can request a new one in ${resendIn}s.` : ""}
                                </p>
                            )}
                            <p className="text-[11px] text-[#6E7A74] px-1">
                                Prefer one tap? The same email has a "Sign me in" link — it works on this or any device.
                            </p>
                        </form>
                    )}

                    {step === "password" && (
                        <form onSubmit={passwordSignIn} className="space-y-3" noValidate data-testid="login-form">
                            <p className="text-xs text-[#6E7A74] px-1" data-testid="login-pass-email-echo">
                                Signing in as <strong className="text-[#1F2624]">{cleanEmail()}</strong>{" "}
                                <button type="button" className="underline text-teal-dark" data-testid="login-change-email-pass"
                                    onClick={() => { setStep("email"); setPassword(""); }}>change</button>
                            </p>
                            <input
                                type="password"
                                data-testid="login-password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="Your password"
                                className="ml-input w-full"
                                autoComplete="current-password"
                                autoFocus
                                required
                            />
                            <button
                                type="submit"
                                disabled={busy}
                                data-testid="login-submit"
                                className="w-full bg-teal-dark hover:bg-teal-dark/90 text-white rounded-2xl px-5 py-3 text-sm font-medium min-h-[44px] disabled:opacity-50"
                            >
                                {busy ? "Signing in…" : "Sign in"}
                            </button>
                            <div className="pt-1">
                                <button
                                    type="button"
                                    onClick={sendCodeInstead}
                                    disabled={busy}
                                    className="text-xs text-teal-dark hover:text-teal-dark underline underline-offset-2 disabled:opacity-50"
                                    data-testid="login-send-code-instead"
                                >
                                    Email me a one-time code instead
                                </button>
                            </div>
                        </form>
                    )}
                </section>

                {/* 2) Partner access code */}
                <section
                    className="ml-card p-6 md:p-7 mb-8 !border-teal-light bg-teal-soft/30"
                    data-testid="login-access-code-block"
                >
                    {!acExpanded ? (
                        <button
                            type="button"
                            onClick={() => setAcExpanded(true)}
                            data-testid="login-access-code-toggle"
                            className="w-full flex items-center gap-3 text-left group"
                        >
                            <span className="w-10 h-10 rounded-2xl bg-teal-dark text-white flex items-center justify-center shrink-0">
                                <Stethoscope strokeWidth={1.7} size={16} />
                            </span>
                            <div className="flex-1 min-w-0">
                                <h2 className="font-heading font-medium text-lg text-[#1F2624]">
                                    I have an access code from my provider
                                </h2>
                                <p className="text-sm text-[#4C5551] mt-0.5">
                                    Click to enter your code and set up your account.
                                </p>
                            </div>
                            <span className="text-xs font-semibold text-teal-dark px-3 py-1 rounded-2xl bg-white border border-teal-light shrink-0">
                                Tap to open
                            </span>
                        </button>
                    ) : (
                        <>
                            <div className="flex items-start gap-3 mb-3">
                                <span className="w-10 h-10 rounded-2xl bg-teal-dark text-white flex items-center justify-center shrink-0">
                                    <Stethoscope strokeWidth={1.7} size={16} />
                                </span>
                                <div>
                                    <h2 className="font-heading font-medium text-lg text-[#1F2624]">
                                        {acStep === "code"
                                            ? "Enter your provider access code"
                                            : `Set up your account · ${acPartner}`}
                                    </h2>
                                    <p className="text-sm text-[#4C5551] mt-0.5">
                                        {acStep === "code"
                                            ? "Your clinic or hospice gave you a short code. It looks like ML-XXXX."
                                            : `${acPartner} referred you to Memory Lane. One more step.`}
                                    </p>
                                </div>
                            </div>

                            {acStep === "code" && (
                                <form onSubmit={verifyCode} className="space-y-3" noValidate>
                                    <input
                                        type="text"
                                        data-testid="login-access-code-input"
                                        value={acCode}
                                        onChange={(e) => setAcCode(e.target.value.toUpperCase())}
                                        placeholder="ML-XXXX"
                                        className="ml-input w-full font-mono tracking-widest text-center text-lg uppercase"
                                        autoComplete="off"
                                        autoCapitalize="characters"
                                        maxLength={32}
                                    />
                                    <button
                                        type="submit"
                                        disabled={acBusy}
                                        data-testid="login-access-code-verify"
                                        className="w-full bg-teal-dark hover:bg-teal-dark/90 text-white rounded-2xl px-5 py-3 text-sm font-medium min-h-[44px] disabled:opacity-50"
                                    >
                                        {acBusy ? "Checking…" : "Continue"}
                                    </button>
                                </form>
                            )}

                            {acStep === "create" && (
                                <>
                                    <div className="flex items-center gap-2 mb-3 px-3 py-2 rounded-2xl bg-white border border-teal-light text-xs text-teal-dark" data-testid="login-access-code-verified-chip">
                                        <Check size={12} strokeWidth={2.4} />
                                        <span><strong>{acPartner}</strong> verified · code <span className="font-mono">{acCode.toUpperCase()}</span></span>
                                        <button
                                            type="button"
                                            onClick={() => { setAcStep("code"); setAcPartner(""); }}
                                            data-testid="login-access-code-change"
                                            className="ml-auto text-[10px] uppercase tracking-wider font-semibold underline"
                                        >
                                            change
                                        </button>
                                    </div>
                                    <form onSubmit={registerWithCode} className="space-y-3" noValidate>
                                        <input
                                            type="text"
                                            data-testid="login-access-code-name"
                                            value={acForm.name}
                                            onChange={(e) => setAcForm({ ...acForm, name: e.target.value })}
                                            placeholder="Your name"
                                            className="ml-input w-full"
                                            autoComplete="name"
                                        />
                                        <input
                                            type="email"
                                            data-testid="login-access-code-email"
                                            value={acForm.email}
                                            onChange={(e) => setAcForm({ ...acForm, email: e.target.value })}
                                            placeholder="you@example.com"
                                            className="ml-input w-full"
                                            autoComplete="email"
                                        />
                                        <input
                                            type="password"
                                            data-testid="login-access-code-password"
                                            value={acForm.password}
                                            onChange={(e) => setAcForm({ ...acForm, password: e.target.value })}
                                            placeholder="Choose a password (8+ characters)"
                                            className="ml-input w-full"
                                            autoComplete="new-password"
                                            minLength={8}
                                        />
                                        <button
                                            type="submit"
                                            disabled={acBusy}
                                            data-testid="login-access-code-submit"
                                            className="w-full bg-teal-dark hover:bg-teal-dark/90 text-white rounded-2xl px-5 py-3 text-sm font-medium min-h-[44px] disabled:opacity-50 inline-flex items-center justify-center gap-2"
                                        >
                                            <ShieldCheck size={14} strokeWidth={1.8} />
                                            {acBusy ? "Creating your account…" : "Create my account"}
                                        </button>
                                    </form>
                                </>
                            )}
                        </>
                    )}
                </section>
            </main>
        </div>
    );
}
